/**
 * BookingPage
 * -----------
 * Public booking workflow that guides guests through selecting a city, rage room,
 * and 30-minute session before submitting booking details for up to four guests.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';

import useApi from '../hooks/useApi';

const INITIAL_CUSTOMER_DETAILS = {
  name: '',
  email: '',
  phoneNumber: '',
  verificationCode: '',
  specialNote: '',
};

const MAX_PARTY_SIZE = 4;
const PLUTO_LOGO_PATH = `${process.env.PUBLIC_URL || ''}/PlutoTV_Logo_1080x1080_RGB_Plutonium.ai.png`;

const rowsFromResponse = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response.rows)) {
    return response.rows;
  }
  if (Array.isArray(response.data?.rows)) {
    return response.data.rows;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response.items)) {
    return response.items;
  }
  return [];
};

const formatDisplayDate = (value) => {
  if (!value) {
    return 'Date TBD';
  }
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatDisplayTime = (timeSlot) => {
  if (!timeSlot) {
    return 'Time TBD';
  }
  const [hourStr, minuteStr] = timeSlot.split(':');
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return timeSlot;
  }
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const slotDateValue = (slot) => {
  if (!slot?.date) {
    return Number.NaN;
  }
  const dateString = `${slot.date}T${slot.timeSlot || '00:00'}:00`;
  const parsed = Date.parse(dateString);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }
  return Date.parse(slot.date);
};

const getSlotIdentifier = (slot) => {
  if (!slot) {
    return 'unknown-slot';
  }
  return `${slot.roomId}|${slot.date}|${slot.sessionLabel}|${slot.slotLabel}`;
};

const sortSlots = (a, b) => {
  const aValue = slotDateValue(a);
  const bValue = slotDateValue(b);
  if (!Number.isNaN(aValue) && !Number.isNaN(bValue)) {
    return aValue - bValue;
  }
  if (!Number.isNaN(aValue)) {
    return -1;
  }
  if (!Number.isNaN(bValue)) {
    return 1;
  }
  return getSlotIdentifier(a).localeCompare(getSlotIdentifier(b));
};

const BookingPage = () => {
  const { rageRoom } = useApi();
  const locationFilter = (process.env.REACT_APP_DEFAULT_LOCATION_ID || '').trim();

  const [availabilityRows, setAvailabilityRows] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState(null);

  const [selectedCity, setSelectedCity] = useState('');
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [partySize, setPartySize] = useState(1);
  const [customerDetails, setCustomerDetails] = useState({ ...INITIAL_CUSTOMER_DETAILS });

  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(null);

  const loadAvailability = useCallback(async () => {
    if (!rageRoom?.public?.listAvailability) {
      setAvailabilityError('Availability service is not configured.');
      setAvailabilityLoading(false);
      return;
    }

    setAvailabilityLoading(true);
    setAvailabilityError(null);

    try {
      const today = new Date();
      const startDate = today.toISOString().split('T')[0];
      const rangeEnd = new Date(today);
      rangeEnd.setDate(rangeEnd.getDate() + 14);
      const endDate = rangeEnd.toISOString().split('T')[0];

      const availabilityParams = {
        startDate,
        endDate,
        onlyAvailable: true,
      };

      if (locationFilter) {
        availabilityParams.locationId = locationFilter;
      }

      const response = await rageRoom.public.listAvailability(availabilityParams);

      setAvailabilityRows(rowsFromResponse(response));
    } catch (error) {
      setAvailabilityError(error.message || 'Unable to load availability right now.');
      setAvailabilityRows([]);
    } finally {
      setAvailabilityLoading(false);
    }
  }, [locationFilter]);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  const cities = useMemo(() => {
    const unique = new Set();
    const ordered = [];
    availabilityRows.forEach((row) => {
      const cityName = row.city || 'Unknown City';
      if (unique.has(cityName)) {
        return;
      }
      unique.add(cityName);
      ordered.push(cityName);
    });
    return ordered.sort((a, b) => a.localeCompare(b));
  }, [availabilityRows]);

  const filteredRowsForCity = useMemo(() => {
    if (!selectedCity) {
      return [];
    }
    return availabilityRows.filter((row) => (row.city || 'Unknown City') === selectedCity);
  }, [availabilityRows, selectedCity]);

  const roomsForSelectedCity = useMemo(() => {
    if (!filteredRowsForCity.length) {
      return [];
    }
    const grouped = new Map();
    filteredRowsForCity.forEach((row) => {
      if (!grouped.has(row.roomId)) {
        grouped.set(row.roomId, {
          id: row.roomId,
          name: row.roomName,
          city: row.city || selectedCity,
          locationId: row.locationId,
          slots: [],
        });
      }
      grouped.get(row.roomId).slots.push(row);
    });
    return Array.from(grouped.values())
      .map((room) => {
        const sortedSlots = [...room.slots].sort(sortSlots);
        return {
          ...room,
          availableCount: room.slots.length,
          nextSlot: sortedSlots[0],
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredRowsForCity, selectedCity]);

  const availableSlotsForRoom = useMemo(() => {
    if (!selectedRoomId) {
      return [];
    }
    return filteredRowsForCity.filter((row) => row.roomId === selectedRoomId).sort(sortSlots);
  }, [filteredRowsForCity, selectedRoomId]);

  const selectedRoom = useMemo(
    () => roomsForSelectedCity.find((room) => room.id === selectedRoomId) || null,
    [roomsForSelectedCity, selectedRoomId]
  );

  const selectionSummary = useMemo(() => {
    if (!selectedSlot) {
      return '';
    }
    return `${selectedSlot.roomName} • ${formatDisplayDate(selectedSlot.date)} @ ${formatDisplayTime(
      selectedSlot.timeSlot
    )}`;
  }, [selectedSlot]);

  const canSubmitBooking = useMemo(() => {
    if (!selectedSlot) {
      return false;
    }
    return Boolean(
      customerDetails.name.trim() &&
        customerDetails.email.trim() &&
        customerDetails.phoneNumber.trim() &&
        customerDetails.verificationCode.trim()
    );
  }, [customerDetails, selectedSlot]);

  const handleCitySelection = (city) => {
    setSelectedCity(city);
    setSelectedRoomId('');
    setSelectedSlot(null);
    setBookingSuccess(null);
    setBookingError(null);
  };

  const handleRoomSelection = (roomId) => {
    setSelectedRoomId(roomId);
    setSelectedSlot(null);
    setBookingSuccess(null);
    setBookingError(null);
  };

  const handleSlotSelection = (slot) => {
    setSelectedSlot(slot);
    setBookingSuccess(null);
    setBookingError(null);
  };

  const handleCustomerChange = (field) => (event) => {
    setCustomerDetails((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handlePartySizeChange = (event) => {
    const value = Number(event.target.value);
    if (Number.isNaN(value)) {
      setPartySize(1);
      return;
    }
    setPartySize(Math.min(MAX_PARTY_SIZE, Math.max(1, value)));
  };

  const handleSubmitBooking = async (event) => {
    event.preventDefault();

    if (!selectedSlot) {
      setBookingError('Please select a time slot before booking.');
      return;
    }

    if (!rageRoom?.public?.bookRoom) {
      setBookingError('Booking service is not available right now.');
      return;
    }

    setBookingLoading(true);
    setBookingError(null);
    setBookingSuccess(null);

    try {
      const compiledNotes = [`Party size: ${partySize}`];
      if (customerDetails.specialNote.trim()) {
        compiledNotes.push(customerDetails.specialNote.trim());
      }

      await rageRoom.public.bookRoom({
        locationId: selectedSlot.locationId,
        roomId: selectedSlot.roomId,
        date: selectedSlot.date,
        sessionLabel: selectedSlot.sessionLabel,
        timeSlot: selectedSlot.timeSlot,
        slotLabel: selectedSlot.slotLabel,
        customer: {
          name: customerDetails.name.trim(),
          email: customerDetails.email.trim(),
          phoneNumber: customerDetails.phoneNumber.trim(),
        },
        verificationCode: customerDetails.verificationCode.trim(),
        specialNote: compiledNotes.join(' • '),
      });

      setBookingSuccess(
        `Your smash squad is confirmed for ${selectedSlot.roomName} on ${formatDisplayDate(
          selectedSlot.date
        )} at ${formatDisplayTime(selectedSlot.timeSlot)}.`
      );
      setCustomerDetails({ ...INITIAL_CUSTOMER_DETAILS });
      setPartySize(1);
      await loadAvailability();
    } catch (error) {
      setBookingError(error.message || 'We could not confirm your booking. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Stack spacing={4}>
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', sm: 'center' },
              mb: 3,
            }}
          >
            <Box
              component="img"
              src={PLUTO_LOGO_PATH}
              alt="Pluto TV logo"
              sx={{
                height: { xs: 56, sm: 72, md: 160 },
                width: 'auto',
                filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.6))',
              }}
            />
          </Box>
          <Chip
            label="30-Min Private Rage Sessions"
            color="primary"
            sx={{ mb: 2, fontWeight: 600 }}
          />
          <Typography variant="h3" gutterBottom>
            Book a Rage Room for Free courtesy of Pluto TV
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 640 }}>
            Choose your city, lock in a room, and reserve a 30-minute smash session for up to four people.
            All availability is live and updates the moment bookings are confirmed.
          </Typography>
        </Box>

        {availabilityError && (
          <Alert severity="error" onClose={() => setAvailabilityError(null)}>
            {availabilityError}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Typography variant="h5">1. Choose a City</Typography>
                <Button variant="outlined" onClick={loadAvailability} disabled={availabilityLoading}>
                  Refresh Availability
                </Button>
              </Stack>
              {availabilityLoading ? (
                <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : cities.length === 0 ? (
                <Typography color="text.secondary">
                  No public sessions are available right now. Check back soon for new drops.
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {cities.map((city) => (
                    <Chip
                      key={city}
                      label={city}
                      color={selectedCity === city ? 'primary' : 'default'}
                      variant={selectedCity === city ? 'filled' : 'outlined'}
                      onClick={() => handleCitySelection(city)}
                      sx={{ height: 36, fontWeight: 600 }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              2. Pick a Rage Room
            </Typography>
            {!selectedCity ? (
              <Typography color="text.secondary">
                Select a city above to see which rooms are open for booking.
              </Typography>
            ) : roomsForSelectedCity.length === 0 ? (
              <Typography color="text.secondary">
                We are resetting inventory in {selectedCity}. Please choose another city to continue.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {roomsForSelectedCity.map((room) => {
                  const slotDescriptor = room.nextSlot
                    ? `${formatDisplayDate(room.nextSlot.date)} @ ${formatDisplayTime(room.nextSlot.timeSlot)}`
                    : 'Scheduling…';
                  const availabilityChipColor = room.availableCount > 5 ? 'success' : 'warning';

                  return (
                    <Grid item xs={12} md={6} key={room.id}>
                      <Card
                        onClick={() => handleRoomSelection(room.id)}
                        sx={{
                          height: '100%',
                          borderWidth: selectedRoomId === room.id ? 2 : 1,
                          borderStyle: 'solid',
                          borderColor: selectedRoomId === room.id ? 'primary.main' : 'divider',
                          cursor: 'pointer',
                          transition: 'border-color 0.2s ease, transform 0.2s ease',
                          transform: selectedRoomId === room.id ? 'translateY(-2px)' : 'none',
                        }}
                        variant="outlined"
                      >
                        <CardContent>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 1 }}
                          >
                            <Typography variant="h6">{room.name}</Typography>
                            <Chip label={`${room.availableCount} slots`} size="small" color={availabilityChipColor} />
                          </Stack>
                          <Typography color="text.secondary" sx={{ mb: 1 }}>
                            {room.city} • Next slot {slotDescriptor}
                          </Typography>
                          <Typography variant="body2">
                            Tap to review all 30-minute sessions in this room.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              3. Lock a 30-Minute Slot
            </Typography>
            {!selectedRoom ? (
              <Typography color="text.secondary">
                Choose a room to browse the remaining sessions for that location.
              </Typography>
            ) : availableSlotsForRoom.length === 0 ? (
              <Typography color="text.secondary">
                This room is fully committed. Pick another room or refresh availability.
              </Typography>
            ) : (
              <Box sx={{ mt: 1, width: '100%' }}>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    mx: 'auto',
                    height: 400,
                    overflowY: 'auto',
                    backgroundColor: 'background.paper',
                    p: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      justifyContent: 'flex-start',
                    }}
                  >
                    {availableSlotsForRoom.map((slot) => {
                      const slotId = getSlotIdentifier(slot);
                      const isSelected = selectedSlot && getSlotIdentifier(selectedSlot) === slotId;

                      return (
                        <Card
                          key={slotId}
                          variant="outlined"
                          sx={{
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            flex: '1 1 260px',
                            minWidth: 220,
                            maxWidth: 320,
                          }}
                        >
                          <CardContent sx={{ py: 1.5, px: 2 }}>
                            <Stack
                              direction="column"
                              spacing={0.5}
                              sx={{ mb: 1 }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {formatDisplayDate(slot.date)} • {formatDisplayTime(slot.timeSlot)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {slot.sessionLabel} • {slot.remainingCapacity} spot
                                {slot.remainingCapacity === 1 ? '' : 's'} left
                              </Typography>
                            </Stack>
                            <Button
                              variant={isSelected ? 'contained' : 'outlined'}
                              color={isSelected ? 'primary' : 'inherit'}
                              onClick={() => handleSlotSelection(slot)}
                              disabled={slot.remainingCapacity <= 0}
                              size="small"
                              fullWidth
                            >
                              {isSelected ? 'Selected' : 'Select Slot'}
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card>
          {selectedSlot ? (
            <Box component="form" noValidate onSubmit={handleSubmitBooking}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  4. Confirm Your Crew
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {selectionSummary} • 30-minute private run for up to {MAX_PARTY_SIZE} people.
                </Alert>
                {bookingSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBookingSuccess(null)}>
                    {bookingSuccess}
                  </Alert>
                )}
                {bookingError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBookingError(null)}>
                    {bookingError}
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Full Name"
                      value={customerDetails.name}
                      onChange={handleCustomerChange('name')}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      type="email"
                      value={customerDetails.email}
                      onChange={handleCustomerChange('email')}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number"
                      type="tel"
                      value={customerDetails.phoneNumber}
                      onChange={handleCustomerChange('phoneNumber')}
                      required
                      fullWidth
                      helperText="We'll text your confirmation here."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Verification Code"
                      value={customerDetails.verificationCode}
                      onChange={handleCustomerChange('verificationCode')}
                      required
                      fullWidth
                      helperText="Enter the SMS code you received to verify your crew."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Party Size"
                      value={partySize}
                      onChange={handlePartySizeChange}
                      fullWidth
                      helperText="Up to 4 guests per smash window."
                    >
                      {Array.from({ length: MAX_PARTY_SIZE }, (_, index) => index + 1).map((count) => (
                        <MenuItem key={count} value={count}>
                          {count} {count === 1 ? 'Person' : 'People'}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Anything we should know?"
                      value={customerDetails.specialNote}
                      onChange={handleCustomerChange('specialNote')}
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Drop special requests, playlists, or accessibility notes."
                    />
                  </Grid>
                </Grid>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="flex-end"
                  spacing={2}
                  sx={{ mt: 3 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!canSubmitBooking || bookingLoading}
                  >
                    {bookingLoading ? 'Booking...' : 'Confirm Booking'}
                  </Button>
                </Stack>
              </CardContent>
            </Box>
          ) : (
            <CardContent>
              <Typography variant="h5" gutterBottom>
                4. Confirm Your Crew
              </Typography>
              <Typography color="text.secondary">
                Select a time slot above to unlock the booking form.
              </Typography>
            </CardContent>
          )}
        </Card>
      </Stack>
    </Container>
  );
};

export default BookingPage;